import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

interface LoginResponse {
  message: string;
  userId: number;
  isSetPassword: boolean;
  isSavedProfile: boolean;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [system, setSystem] = useState("ors");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // レスポンシブ対応のため、ウィンドウサイズの変更を監視
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const systemOptions = isMobile
    ? [{ value: "ors", label: "出社予約" }]
    : [
        { value: "tassei", label: "目標管理" },
        { value: "ors", label: "出社予約" },
        { value: "tf", label: "スキルマネジメント" },
        { value: "main", label: "プロフィール設定" },
      ];

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    try {
      localStorage.removeItem("authUser");
      const response = await axios.post<LoginResponse>(
        `${process.env.REACT_APP_BACKEND_URL}/api/login`,
        { email, password, system },
        { withCredentials: true },
      );

      setIsAuthenticated(true);

      const userId = response.data.userId;

      // 画面遷移
      if (!response.data.isSetPassword) {
        // プロフィール未設定の場合はプロフィール設定ページへ遷移
        navigate(`/main/change-password`);
      } else if (!response.data.isSavedProfile) {
        // 初回パスワード未設定の場合はパスワード設定ページへ遷移
        navigate(`/main/profile/${userId}`);
      } else if (system === "tassei") {
        window.location.href = `${process.env.REACT_APP_VUE_FRONTEND_URL}/tassei/home`;
      } else if (system === "ors") {
        window.location.href = `${process.env.REACT_APP_VUE_FRONTEND_URL}/ors/home`;
      } else if (system === "tf") {
        navigate("/tf/user-sheets");
      } else {
        navigate(`/main/profile/${userId}`);
      }
    } catch (error: any) {
      // エラーメッセージ設定
      if (error.response) {
        setErrorMessage(
          error.response.data?.error ||
            "ログインに失敗しました。メールアドレスまたはパスワードを確認してください。",
        );
      } else if (error.request) {
        setErrorMessage(
          "サーバーに接続できませんでした。ネットワークを確認してください。",
        );
      } else {
        setErrorMessage("エラーが発生しました: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-lg relative">
        <div className="flex justify-center mb-6">
          {isLoading ? (
            <div className="w-20 h-20 bg-gray-300 rounded-full animate-pulse"></div>
          ) : (
            <img src="/isopra_icon.png" alt="Logo" className="w-20 h-20" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-center text-gray-700 mb-4">
          {isLoading ? (
            <div className="w-32 h-6 bg-gray-300 rounded animate-pulse mx-auto"></div>
          ) : (
            "ログイン"
          )}
        </h2>

        {/* エラーメッセージ表示 */}
        {errorMessage && (
          <p className="text-red-500 text-center mb-4">{errorMessage}</p>
        )}

        <div className={isLoading ? "space-y-4" : ""}>
          {isLoading ? (
            <>
              <div className="w-full h-10 bg-gray-300 rounded animate-pulse"></div>
              <div className="w-full h-10 bg-gray-300 rounded animate-pulse"></div>
              <div className="w-full h-10 bg-gray-300 rounded animate-pulse"></div>
              <div className="w-full h-12 bg-iso rounded animate-pulse"></div>
            </>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-gray-700">メールアドレス</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="example@example.com"
                />
              </div>
              <div>
                <label className="block text-gray-700">パスワード</label>
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="パスワード"
                />
              </div>
              <div>
                <label className="block text-gray-700">システム選択</label>
                <select
                  name="system"
                  value={system}
                  onChange={(e) => setSystem(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isMobile}
                >
                  {systemOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="w-full bg-iso text-white font-bold py-2 rounded-lg hover:bg-opacity-50"
                disabled={isLoading}
              >
                {isLoading ? "ログイン中..." : "ログイン"}
              </button>
              <div className="text-center mt-4">
                <a
                  href="/forgot-password"
                  className="text-blue-500 hover:underline"
                >
                  パスワードを忘れた場合
                </a>
              </div>
            </form>
          )}
        </div>
        <div className="text-center text-xs text-gray-400 mt-8 -mb-7">
          Version 4.1.5
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
